import { useFragment } from "@/generated";
import {
  GetLessonByParentIdDocument,
  GetLessonWithLimitationDocument,
  GetLessonsForNavigationDocument,
  LessonFieldsFragmentDoc,
} from "@/generated/graphql";
import {
  type LessonsByParentOptions,
  getLessonsByParentIdVars,
} from "@/lib/utils/graphql/getLessonsByParentIdVars";
import { useQuery } from "@apollo/client/react";

/**
 * Fetches and paginates lessons belonging to a specific parent project.
 *
 * Queries the GraphQL API for lessons with only neccessary field for navigation
 * (isLinkedCopy , hasLinkedCopy, name, id)
 *
 * @example
 * const { lessons, hasMore, loading, loadMore } = useLessonsForNavigation({
 *   parentId: "project123",
 *   searchText: "math"
 * });
 */
export function useLessonsForNavigation({
  parentId,
  searchText,
  sortBy,
  isTrash = false,
  skip = false,
}: LessonsByParentOptions) {
  const {
    data: lessonsByParentIdData,
    loading,
    fetchMore,
  } = useQuery(GetLessonsForNavigationDocument, {
    variables: getLessonsByParentIdVars({
      parentId,
      searchText,
      sortBy,
      isTrash,
    }),
    skip,
    fetchPolicy: "cache-first",
  });

  const items = lessonsByParentIdData?.lessonsByParentId.items ?? [];
  const hasMore = lessonsByParentIdData?.lessonsByParentId.hasMore ?? false;

  const loadMore = () => {
    fetchMore({
      variables: {
        ...getLessonsByParentIdVars({
          parentId,
          searchText,
          sortBy,
          isTrash,
        }),
        offset: items.length,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        return {
          lessonsByParentId: {
            ...fetchMoreResult.lessonsByParentId,
            items: [...prev.lessonsByParentId.items, ...fetchMoreResult.lessonsByParentId.items],
          },
        };
      },
    });
  };

  return {
    lessons: items,
    hasMore,
    loading,
    loadMore,
  };
}
